/**
 * Umbenannte Antwort-Schlüssel der Immobilien-Erfassung — die EINE Stelle, an
 * der Bestandsdaten auf die heute gültigen Schlüssel gezogen werden.
 *
 * Hintergrund: Die Antworten eines Gebäudes liegen als `RiskCharacteristics`
 * (`nameBezeichnung` = Antwort-Schlüssel). Wird ein Schlüssel umbenannt, ohne
 * die Altfassung mitzulesen, passiert dreierlei — und zwar STILL:
 *  1. Die Frage öffnet sich im Bearbeiten-Modus unbeantwortet, obwohl der Kunde
 *     sie längst beantwortet hat.
 *  2. Alles, was an der Antwort hängt (bei `brandschutzkonzept` der
 *     Hochlade-Bereich), bleibt verborgen — vorhandene Nachweise sind unsichtbar.
 *  3. Beantwortet jemand neu, trägt der Datensatz BEIDE Schlüssel. `PUT
 *     /risk-objects/:id` ist ein Replace-Set, der alte Wert reist unverändert
 *     mit, und auf `/risks/:id` stehen zwei einander widersprechende Zeilen.
 *
 * Deshalb wird beim Laden umgeschrieben statt nur nachgelesen: der Altschlüssel
 * verschwindet aus dem Antwort-Bag und damit beim nächsten Speichern auch aus
 * den `RiskCharacteristics`. Das ist kein Datenverlust — sein Wert steht dann
 * unter dem neuen Schlüssel.
 *
 * Gilt ausdrücklich nur für UMBENENNUNGEN. Ein ersatzlos gestrichener Block
 * (`frueherkennung*`, 19.08.2026) gehört nicht hierher: dort gibt es keinen
 * Nachfolger, auf den sich der Wert abbilden ließe.
 */

/** `alter Schlüssel` → `heutiger Schlüssel`. */
export const UMBENANNTE_ANTWORT_SCHLUESSEL: Readonly<Record<string, string>> = {
  /*
   * 19.08.2026: „Brandschutzkonzept" ist nur noch ja/nein, und die Antwort
   * trägt seither den Hochlade-Bereich — sie sagt aus, ob das DOKUMENT
   * vorliegt. Die früheren Folgefelder `brandschutzVonWem` /
   * `brandschutzDatum` haben keinen Nachfolger und bleiben bewusst
   * unangetastet: sie stehen auf `/risks/:id` im Abschnitt „Sicherheit &
   * Schadenverhütung" weiterhin lesbar und bearbeitbar.
   */
  brandschutzkonzept: "brandschutzDokumentVorhanden",
};

/** Leer im Sinne des Speicherfilters: fehlend, `null` oder blanker Text. */
const istLeer = (wert: unknown): boolean =>
  wert === undefined || wert === null || (typeof wert === "string" && wert.trim() === "");

/**
 * Zieht alte Antwort-Schlüssel eines Bags auf die heutigen um.
 *
 * Der neue Schlüssel gewinnt, wenn beide belegt sind — er ist die jüngere
 * Angabe. Der alte fällt in jedem Fall weg, damit kein zweites, widersprechendes
 * Merkmal zurückgespeichert wird. Idempotent: ein bereits umgezogener Bag kommt
 * unverändert zurück.
 */
export const migriereAltAntworten = (
  answers: Record<string, unknown> | null | undefined,
): Record<string, unknown> => {
  if (!answers) return {};
  const out: Record<string, unknown> = { ...answers };
  for (const [alt, neu] of Object.entries(UMBENANNTE_ANTWORT_SCHLUESSEL)) {
    if (!(alt in out)) continue;
    if (istLeer(out[neu]) && !istLeer(out[alt])) out[neu] = out[alt];
    delete out[alt];
  }
  return out;
};

const istObjekt = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Umzüge INNERHALB der Ausstattungs-Kataloge (10/2026) — `migriereAltAntworten`
 * kennt nur ganze Schlüssel, hier wandern Einträge eines Objekts:
 *
 * - `sonderausstattungen.aufzug` → `sonderausstattungen.personenaufzug`
 *   (der Aufzug ist in Personen- und Lastenaufzug geteilt; ein Bestands-Aufzug
 *   ist fast immer ein Personenaufzug),
 * - `sonderausstattungen.kompressoren` → Anlage `sicherheit_kompressorenLueftung`
 *   (stand doppelt: als Ausstattung und als Anlage im Schritt Sicherheit).
 *
 * Wie oben gewinnt ein bereits belegter neuer Eintrag; der alte fällt weg.
 * Idempotent.
 */
export const migriereAusstattung = (answers: Record<string, unknown>): Record<string, unknown> => {
  const sonder = answers.sonderausstattungen;
  if (!istObjekt(sonder)) return answers;
  if (!("aufzug" in sonder) && !("kompressoren" in sonder)) return answers;
  const out: Record<string, unknown> = { ...answers };
  const neu: Record<string, unknown> = { ...sonder };
  if ("aufzug" in neu) {
    if (!istObjekt(neu.personenaufzug)) neu.personenaufzug = neu.aufzug;
    delete neu.aufzug;
  }
  if ("kompressoren" in neu) {
    const k = neu.kompressoren;
    const anlage = out.sicherheit_kompressorenLueftung;
    if (istObjekt(k) && k.selected === "true" && !istObjekt(anlage)) {
      out.sicherheit_kompressorenLueftung = {
        vorhanden: "ja",
        ...(typeof k.wert === "string" && k.wert ? { wert: k.wert } : {}),
      };
    }
    delete neu.kompressoren;
  }
  out.sonderausstattungen = neu;
  return out;
};
